import React, { useState, useEffect } from 'react';
import { Users, DollarSign, ShoppingCart, AlertTriangle, RefreshCw, Store, BarChart4 } from 'lucide-react';
import Card from '../ui/Card';
import Chart from '../ui/Chart';
import Select from '../ui/Select';
import MetricInfoPopover from '../ui/MetricInfoPopover';
import { fetchVendorDashboardSummary } from '../../../services/MasterAdmin/vendorService';
import {
  vendorPerformanceByDemographic,
} from '../../../data/mockData';
import { vendorDashboardMetricInfo } from '../../../config/masterAdminMetricDefinitions';

const VendorDashboard: React.FC = () => {
  const [timeFilter, setTimeFilter] = useState('monthly');
  const [demographicFilter, setDemographicFilter] = useState('ageGroups');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('ALL');
  const [summary, setSummary] = useState({
    totalRegisteredVendors: 0,
    totalActiveVendors: 0,
    totalBookings: 0,
    vendorCategories: [] as Array<{ role: number; name: string; total: number; active: number }>,
    authSessions: {
      total: 0,
      active: 0,
    },
    fcmTokens: {
      total: 0,
      active: 0,
    },
    performanceOverview: {
      daily: [] as any[],
      weekly: [] as any[],
      monthly: [] as any[],
      quarterly: [] as any[],
    },
    demographics: {
      ageGroups: {} as Record<string, any>,
      gender: {} as Record<string, any>,
      healthCondition: {} as Record<string, any>,
    },
    topPerformingVendors: [] as any[],
  });
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  
  useEffect(() => {
    const loadVendorSummary = async () => {
      try {
        if (summary.vendorCategories.length === 0) {
          setLoading(true);
        } else {
          setChartLoading(true);
        }
        const payload = await fetchVendorDashboardSummary(serviceFilter);
        setSummary(payload);
      } catch (error) {
        console.error('Error loading vendor dashboard summary:', error);
      } finally {
        setLoading(false);
        setChartLoading(false);
      }
    };

    loadVendorSummary();
  }, [serviceFilter]);

  const prepareChartData = (period: string) => {
    const data = summary.performanceOverview?.[period as keyof typeof summary.performanceOverview] || [];
    if (data.length > 0) return data;

    const periods = {
      daily: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      weekly: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'],
      monthly: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      quarterly: ['Q1', 'Q2', 'Q3', 'Q4'],
    };
    const periodLabels = periods[period as keyof typeof periods];
    return periodLabels.map((label) => ({
      name: label,
      bookings: 0,
      revenue: 0,
    }));
  };

  const prepareDemographicData = (filter: string) => {
    const data = summary.demographics?.[filter as keyof typeof summary.demographics] || {};
    const keys = Object.keys(data);
    if (keys.length > 0) {
      return keys.map((key) => ({
        name: key,
        visits: data[key].visits || 0,
        bookings: data[key].bookings || 0,
        revenue: data[key].revenue || 0,
      }));
    }

    const mockCat = vendorPerformanceByDemographic[filter as keyof typeof vendorPerformanceByDemographic] || {};
    return Object.keys(mockCat).map((key) => ({
      name: key,
      visits: 0,
      bookings: 0,
      revenue: 0,
    }));
  };
  
  // Filter options
  const timeFilterOptions = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Quarterly', value: 'quarterly' },
  ];
  
  const demographicFilterOptions = [
    { label: 'Age Group', value: 'ageGroups' },
    { label: 'Gender', value: 'gender' },
    { label: 'Health Condition', value: 'healthCondition' },
  ];

  const categoryFilterOptions = [
    { label: 'All Categories', value: 'all' },
    { label: 'Hospital', value: 'Hospital' },
    { label: 'Clinic', value: 'Clinic' },
    { label: 'Medical Store', value: 'Medical Store' },
    { label: 'Ambulance Agency', value: 'Ambulance Agency' },
    { label: 'Blood Bank', value: 'Blood Bank' },
    { label: 'Diagnostic Center', value: 'Diagnostic Center' },
    { label: 'Delivery Partner', value: 'Delivery Partner' },
    { label: 'Product Partner', value: 'Product Partner' },
  ];

  const serviceFilterOptions = [
    { label: 'All Services', value: 'ALL' },
    { label: 'Hospital', value: 'HOSPITAL' },
    { label: 'Clinic', value: 'CLINIC' },
    { label: 'Medical Store', value: 'MEDICINE' },
    { label: 'Ambulance Agency', value: 'AMBULANCE' },
    { label: 'Blood Bank', value: 'BLOOD_BANK' },
    { label: 'Diagnostic Center', value: 'LAB_TEST' },
    { label: 'Product Partner', value: 'PRODUCT' },
  ];

  // Define custom chart colors for better aesthetics (bookings and revenue)
  const performanceChartColors = ['#f59e0b', '#10b981'];
  const demographicsChartColors = ['#8b5cf6', '#ec4899', '#06b6d4'];

  const totalRegisteredVendors = summary?.totalRegisteredVendors || 0;
  const totalBookings = summary?.totalBookings || 0;
  const activeSessions = summary?.authSessions?.active || 0;
  const activeTokens = summary?.fcmTokens?.active || 0;
  const vendorCategories = summary?.vendorCategories || [];
  const topPerformingVendors = summary?.topPerformingVendors || [];

  const filteredVendors = (categoryFilter === 'all'
    ? topPerformingVendors
    : topPerformingVendors.filter((vendor: any) => vendor.category === categoryFilter)
  ).slice(0, 5);

  return (
    <div className="w-full min-w-0 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          title="Total Registered Vendors"
          value={totalRegisteredVendors.toLocaleString()}
          icon={<Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />}
          info={vendorDashboardMetricInfo.totalRegisteredVendors}
        />
        <Card
          title="Total Bookings"
          value={totalBookings.toLocaleString()}
          icon={<ShoppingCart className="w-6 h-6 text-purple-600 dark:text-purple-400" />}
          info={vendorDashboardMetricInfo.totalBookings}
        />
        <Card
          title="Active Vendor Sessions"
          value={activeSessions.toLocaleString()}
          icon={<DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />}
          info={vendorDashboardMetricInfo.activeVendorSessions}
        />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="col-span-1 bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-900/30 transition-all duration-300">
          <div className="flex items-center mb-4">
            <Store className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-2" />
            <h3 className="text-lg font-medium dark:text-white">Vendor Categories</h3>
            <MetricInfoPopover title="Vendor Categories" info={vendorDashboardMetricInfo.vendorCategories} className="ml-2" />
          </div>
          <div className="space-y-4">
            {loading ? (
              <div className="flex justify-center items-center h-40">
                <RefreshCw className="w-6 h-6 text-primary-600 dark:text-primary-400 animate-spin" />
              </div>
            ) : (
              vendorCategories.map((category) => {
                return (
                  <div key={category.role} className="flex justify-between items-center group hover:bg-gray-50 dark:hover:bg-gray-700/50 p-2 rounded-md transition-colors">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      {category.name}
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                        {category.active} active
                      </span>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">
                        {category.total} total
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        
        <div className="col-span-2">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-900/30 transition-all duration-300">
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 mb-4">
              <div className="flex items-center">
                <BarChart4 className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-2" />
                <h3 className="text-lg font-medium dark:text-white">Performance Overview</h3>
                <MetricInfoPopover title="Performance Overview" info={vendorDashboardMetricInfo.performanceOverview} className="ml-2" />
              </div>
              <div className="flex space-x-2">
                <Select
                  value={serviceFilter}
                  onChange={setServiceFilter}
                  options={serviceFilterOptions}
                />
                <Select
                  value={timeFilter}
                  onChange={setTimeFilter}
                  options={timeFilterOptions}
                />
              </div>
            </div>
            <div className={`transition-opacity duration-200 ${chartLoading ? 'opacity-50 pointer-events-none' : ''}`}>
              <Chart
                info={vendorDashboardMetricInfo.performanceOverview}
                data={prepareChartData(timeFilter)}
                xKey="name"
                yKeys={['bookings', 'revenue']}
                colors={performanceChartColors}
                type="line"
                useDualAxis={true}
                height={380}
              />
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="flex h-full flex-col bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-900/30 transition-all duration-300">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 mb-4">
            <div className="flex items-center">
              <Users className="w-5 h-5 text-secondary-600 dark:text-secondary-400 mr-2" />
              <h3 className="text-lg font-medium dark:text-white">Demographics</h3>
              <MetricInfoPopover title="Demographics" info={vendorDashboardMetricInfo.demographics} className="ml-2" />
            </div>
            <Select
              value={demographicFilter}
              onChange={setDemographicFilter}
              options={demographicFilterOptions}
              className="w-full max-w-[240px] sm:w-auto"
            />
          </div>
          <Chart
            data={prepareDemographicData(demographicFilter)}
            xKey="name"
            yKeys={['visits', 'bookings', 'revenue']}
            colors={demographicsChartColors}
            type="composed"
            useDualAxis={true}
            height={390}
            className="!p-0 !shadow-none"
          />
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-900/30 transition-all duration-300">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 mb-4">
            <div className="flex items-center">
              <Store className="w-5 h-5 text-secondary-600 dark:text-secondary-400 mr-2" />
              <h3 className="text-lg font-medium dark:text-white">Top Performing Vendors</h3>
              <MetricInfoPopover title="Top Performing Vendors" info={vendorDashboardMetricInfo.topPerformingVendors} className="ml-2" />
            </div>
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={categoryFilterOptions}
              className="w-full max-w-[240px] sm:w-auto"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Bookings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center">
                      <div className="flex justify-center items-center h-20">
                        <RefreshCw className="w-6 h-6 text-primary-600 dark:text-primary-400 animate-spin" />
                      </div>
                    </td>
                  </tr>
                ) : filteredVendors.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No top performing vendors found
                    </td>
                  </tr>
                ) : (
                  filteredVendors.map((vendor) => (
                    <tr key={vendor.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{vendor.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{vendor.location}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">
                          {vendor.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {vendor.bookings.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        ₹{vendor.revenue.toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDashboard; 
